import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {handleErrorResponse} from "../utils/util"
import { USER_ROLE } from "../constants/index"
import { StaffService } from "../api/services.gen";
import {
  StaffCreateData,
  StaffUpdateData,
  StaffListData,
  StaffDestroyData,
  StaffDetail,
  StaffCreate,
  StaffDataListResponse,
  StaffListResponse,
  StaffRetrieveData,
  StaffChangeAvatarUpdateData,
} from "../api/types.gen";
import i18n from 'i18next';
import { resetAppState } from "../store/common";

interface StaffCreateWithSuccess extends StaffCreate {
  success?: boolean;
  message?: string;
}
export const submitCreateStaff = createAsyncThunk(
  "submitCreateStaff",
  async (data: StaffCreateData, thunkAPI: any) => {
    try {
      const res: StaffCreateWithSuccess = await StaffService.staffCreate(data);
      if (res.success) {
        return res;
      } else {
        return thunkAPI.rejectWithValue(res.message);
      }
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI, i18n.t('MSG_GLOBAL_CREATE_FAILED'));
    }
  },
);


interface StaffDetailWithSuccess extends StaffDetail {
  success?: boolean;
  message?: string;
}
export const submitEditStaff = createAsyncThunk(
  "submitEditStaff",
  async (data: StaffUpdateData, thunkAPI: any) => {
    try {
      const res: StaffDetailWithSuccess = await StaffService.staffUpdate(data);
      if (res.success) {
        return res;
      } else {
        return thunkAPI.rejectWithValue(res.message);
      }
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI, i18n.t('MSG_GLOBAL_UPDATE_FAILED'));
    }
  },
);
export const changeAvatarHeader = createAsyncThunk(
  "changeAvatarHeader",
  async (data: StaffChangeAvatarUpdateData, thunkAPI: any) => {
    try {
      const res: StaffDetailWithSuccess = await StaffService.staffChangeAvatarUpdate(data);
      if (res.success) {
        return res;
      } else {
        return thunkAPI.rejectWithValue(res.message);
      }
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
    }
  },
);
export const changeAvatarStaff = createAsyncThunk(
  "changeAvatarStaff",
  async (data: StaffUpdateData, thunkAPI: any) => {
    try {
      const res: StaffDetailWithSuccess = await StaffService.staffUpdate(data);
      if (res.success) {
        res.message = i18n.t('MSG_GLOBAL_SAVED');
        return res;
      } else {
        return thunkAPI.rejectWithValue(res.message);
      }
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
    }
  },
);


interface StaffDataListResponseWithSuccess extends StaffDataListResponse {
  success?: boolean;
  message?: string;
  data?: any;
}
export const fetchStaffBasicData = createAsyncThunk(
  "fetchStaffBasicData",
  async (navigate: any, thunkAPI: any) => {
    try {
      const res: StaffDataListResponseWithSuccess = await StaffService.staffDataList();
      if (res.success) {
        return res.data;
      } else {
        return thunkAPI.rejectWithValue(res.message);
      }
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
    }
  },
);


interface StaffListResponseWithSuccess extends StaffListResponse {
  success?: boolean;
  message?: string;
  data?: any;
}
export const fetchStaff = createAsyncThunk(
  "fetchStaff",
  async (data: StaffListData = {}, thunkAPI: any) => {
    try {
      const res: any = await StaffService.staffList(data);
      if (res.results.success) {
        return res;
      } else {
        return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
      }
    } catch (error: any) {
      if(error.status == 404){
        return thunkAPI.dispatch(setCurrentPage(thunkAPI.getState().companyStaff.currentPage - 1));
      }
      else {
        return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
      }
    }
  },
);

export const deleteStaff = createAsyncThunk(
  "deleteStaff",
  async (data: StaffDestroyData, thunkAPI: any) => {
    try {
      const res = await StaffService.staffDestroy(data);
      return res
    } catch (error: any) {
      if(error.status == 400) {
        return thunkAPI.rejectWithValue(i18n.t('MSG_STAFF_DELETE_ERROR'));
      }
      else {
        return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
      }
    }
  },
);

export const getStaffDetail = createAsyncThunk(
  "getStaffDetail",
  async (data: StaffRetrieveData, thunkAPI: any) => {
    try {
      const res = await StaffService.staffRetrieve(data);
      return res
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
    }
  },
);

interface Staff {
  first_name: string;
  last_name: string;
  email: string;
  department: any;
  position: any;
  type: any;
  password: string;
  password2: string;
  can_man_talent: boolean;
  can_man_action: boolean;
  can_man_job: boolean;
  can_man_news: boolean;
  actions: Array<{ name: string; action: (staff: Staff) => void }>;
  actionStatus: string;
  actionName: string;
}

export interface TableHeader {
  title: string;
  isSort: boolean;
  field: string;
  currentSortState: number
}

// Initial state of Redux
interface StaffState {
  staffList: Staff[];
  staffBasicData: Staff[];
  loading: boolean;
  error: string | null;
  success: string | null;
  errorAvatar: string | null;
  successAvatar: string | null;
  isPanelOpen: boolean;
  selectedStaff: null;
  sortBy: string | null;
  sortOrder: string | null;
  isOnConfirmDelete: boolean;
  confirmText: any;
  deleteId: number;
  searchQuery: string;
  passOpen: boolean;
  countItems: number;
  countPage: number;
  currentPage: number;
  pageSize: number;
  isReloadStaff: boolean;
  tableHeaders: TableHeader[];
  actionStatus: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  actionName: "",
}

const initTableHeaders = [
  {
    title: "氏名",
    isSort: false,
    field: "",
    currentSortState: 0,
  },
  {
    title: "所属",
    isSort: true,
    field: "department",
    currentSortState: 0,
  },
  {
    title: "役職",
    isSort: true,
    field: "position",
    currentSortState: 0,
  },
  {
    title: "メールアドレス",
    isSort: false,
    field: "",
    currentSortState: 0,
  },
  {
    title: "権限",
    isSort: true,
    field: "type",
    currentSortState: 0,
  },
  {
    title: "追加日",
    isSort: false,
    field: "",
    currentSortState: 0,
  },
  {
    title: "最終更新日時",
    isSort: false,
    field: "",
    currentSortState: 0,
  },
  {
    title: "",
    isSort: false,
    field: "",
    currentSortState: 0,
  },
]

const initialState: StaffState = {
  staffList: [],
  staffBasicData: [],
  loading: false,
  error: null,
  success: null,
  errorAvatar: null,
  successAvatar: null,
  isPanelOpen: false,
  selectedStaff: null,
  sortBy: null,
  sortOrder: null,
  isOnConfirmDelete: false,
  confirmText: null,
  deleteId: 0,
  searchQuery: "",
  passOpen: false,
  countItems: 0,
  countPage: 1,
  currentPage: 1,
  pageSize: 10,
  isReloadStaff: false,
  tableHeaders: initTableHeaders,
};

const sortingStates = ["default", "desc", "asc"];

export const companyStaffSlice = createSlice({
  name: "companyStaff",
  initialState,
  reducers: {
    addActionsToStaff(state) {
      state.staffList = state.staffList.map((staff) => ({
        ...staff,
        actions: [
          {
            name: "削除",
            action: (staff) => {},
          },
        ],
      }));
    },

    handleSortByHeader(state, action) {
      const sortHeader = action.payload;
      state.tableHeaders = state.tableHeaders.map((header, idx) => (header.field === sortHeader.field)
      ? ({...header, currentSortState: (header.currentSortState + 1) % sortingStates.length})
      : ({...header, currentSortState: 0}));
      
      if((sortHeader.currentSortState + 1) % sortingStates.length !== 0) {
        state.sortBy = sortHeader.field;
        state.sortOrder = sortingStates[(sortHeader.currentSortState + 1) % sortingStates.length];
      } else {
        state.sortBy = null;
        state.sortOrder = null;
      }
    },

    togglePanel(state) {
      state.isPanelOpen = true;
    },

    setIsOpenPanel(state, action) {
      state.isPanelOpen = action.payload;
    },

    setSelectedStaff(state, action) {
      state.selectedStaff = action.payload;
      state.isPanelOpen = true;
    },

    setSelectedStaffHeader(state, action) {
      state.selectedStaff = action.payload;
    },

    clearSelectedStaff(state) {
      state.selectedStaff = null;
      state.isPanelOpen = false;
    },

    setSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action) => {
      state.sortOrder = action.payload;
    },
    setOnConfirmDelete: (state, action) => {
      state.isOnConfirmDelete = action.payload;
      if (!action.payload) state.deleteId = 0;
    },
    setDeleteId: (state, action) => {
      state.deleteId = action.payload;
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    setConfirmText: (state, action) => {
      state.confirmText = action.payload;
    },
    setPassOpen: (state, action) => {
      state.passOpen = !state.passOpen;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setIsReloadStaff: (state, action) => {
      state.isReloadStaff = !action.payload;
    },
    setSuccessAvatar: (state, action) => {
      state.successAvatar = action.payload;
    },
    setErrorAvatar: (state, action) => {
      state.errorAvatar = action.payload;
    },
    setSuccess: (state, action) => {
      state.success = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setActionStatus: (state, action) => {
      state.actionStatus = action.payload;
    },
    setActionName: (state, action) => {
      state.actionName = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // reset state
      .addCase(resetAppState, () => initialState)

      // fetch fetchStaff
      .addCase(fetchStaff.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.error = null;
        state.actionName = "fetchStaff";
      })
      .addCase(fetchStaff.fulfilled, (state, action) => {
        state.loading = false;
        state.staffList = action.payload?.results?.data || [];
        companyStaffSlice.caseReducers.addActionsToStaff(state);
        state.countItems = action.payload?.count;
        state.countPage = Math.ceil(state.countItems / state.pageSize);
      })
      .addCase(fetchStaff.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.actionStatus = "rejected";
      })

      // fetch fetchStaffBasicData
      .addCase(fetchStaffBasicData.pending, (state) => {
        state.loading = true;
        state.success = null;
      })
      .addCase(fetchStaffBasicData.fulfilled, (state, action) => {
        state.loading = false;
        state.staffBasicData = action.payload;
      })
      .addCase(fetchStaffBasicData.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = null;
      })

      //fetch deleteStaff
      .addCase(deleteStaff.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deleteStaff.fulfilled, (state, action) => {
        state.deleteId = 0;
        state.loading = false;
        if (action.payload?.success) {
          state.success = action.payload?.message;
        } else {
          state.error = action.payload?.message;
        }
        if (parseInt(state.staffList.length) - 1 == 0) {
          state.currentPage = state.currentPage > 1 ? state.currentPage - 1 : 1;
        }
        state.isReloadStaff = !state.isReloadStaff;
      })
      .addCase(deleteStaff.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = null;
      })

      //fetch submitCreateStaff
      .addCase(submitCreateStaff.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(submitCreateStaff.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload?.message;
        companyStaffSlice.caseReducers.clearSelectedStaff(state);
        state.currentPage = 1; //reload page with newest updated
        state.isReloadStaff = !state.isReloadStaff;
      })
      .addCase(submitCreateStaff.rejected, (state, action) => {
        state.loading = false;
        state.success = null;
        state.error = action.payload as string;
      })

      //fetch submitEditStaff
      .addCase(submitEditStaff.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(submitEditStaff.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload?.message;
        state.selectedStaff = action.payload?.data;
        //companyStaffSlice.caseReducers.clearSelectedStaff(state);
        state.isPanelOpen = false;
        state.currentPage = 1; //reload page with newest updated
        state.isReloadStaff = !state.isReloadStaff;
      })
      .addCase(submitEditStaff.rejected, (state, action) => {
        state.loading = false;
        state.success = null;
        state.error = action.payload as string;
      })

      //getStaffDetail
      .addCase(getStaffDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(getStaffDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedStaff = action.payload;
        state.isPanelOpen = false;
      })
      .addCase(getStaffDetail.rejected, (state, action) => {
        state.loading = false;
        state.success = null;
        state.error = action.payload as string;
      })

      //changeAvatarHeader
      .addCase(changeAvatarHeader.pending, (state) => {
        state.loading = true;
        state.errorAvatar = null;
        state.successAvatar = null;
      })
      .addCase(changeAvatarHeader.fulfilled, (state, action: any) => {
        state.loading = false;
        state.successAvatar = action.payload?.message;
        state.selectedStaff = action.payload?.data;
        state.currentPage = 1; //reload page with newest updated
        state.isReloadStaff = !state.isReloadStaff;
      })
      .addCase(changeAvatarHeader.rejected, (state, action) => {
        state.loading = false;
        state.successAvatar = null;
        state.errorAvatar = action.payload as string;
      })

      //fetch changeAvatarStaff
      .addCase(changeAvatarStaff.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(changeAvatarStaff.fulfilled, (state, action: any) => {
        state.loading = false;
        state.success = action.payload?.message;
        state.selectedStaff = action.payload?.data;
        state.currentPage = 1; //reload page with newest updated
        state.isReloadStaff = !state.isReloadStaff;
      })
      .addCase(changeAvatarStaff.rejected, (state, action) => {
        state.loading = false;
        state.success = null;
        state.error = action.payload as string;
      })
  },
});

export const {
  addActionsToStaff,
  setSelectedStaff,
  clearSelectedStaff,
  togglePanel,
  setSortBy,
  setSortOrder,
  setOnConfirmDelete,
  setDeleteId,
  setSearchQuery,
  setConfirmText,
  setPassOpen,
  setCurrentPage,
  setIsReloadStaff,
  setSelectedStaffHeader,
  setSuccessAvatar,
  setErrorAvatar,
  handleSortByHeader,
  setSuccess,
  setError,
  setIsOpenPanel,
  setActionName,
  setActionStatus,
} = companyStaffSlice.actions;
export default companyStaffSlice.reducer;
